"use client";

import { useState } from "react";
import { useScrollAnimate } from "../lib/hooks";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const MK_BG = "#0f0f11";
const MK_CARD = "rgba(255,255,255,0.04)";
const MK_BD = "rgba(255,255,255,0.06)";
const GREEN = "#22c55e";

// Budget slider runs on a log scale so $750-$5,000 tests get as much slider
// room as six-figure budgets. $750 is 30 days at OpenAI's US minimum daily
// budget of $25 per campaign (help.openai.com 20001210, October 2026).
const BUDGET_MIN = 750;
const BUDGET_MAX = 200000;
const BUDGET_STEPS = 1000;
const DEFAULT_BUDGET = 5000;

const budgetFromPos = (pos) => {
  const raw = BUDGET_MIN * Math.pow(BUDGET_MAX / BUDGET_MIN, pos / BUDGET_STEPS);
  const step = raw < 2000 ? 50 : raw < 10000 ? 250 : raw < 50000 ? 1000 : 5000;
  return Math.max(BUDGET_MIN, Math.min(BUDGET_MAX, Math.round(raw / step) * step));
};
const posFromBudget = (budget) =>
  Math.round((BUDGET_STEPS * Math.log(budget / BUDGET_MIN)) / Math.log(BUDGET_MAX / BUDGET_MIN));

const fmt = (n) => Math.round(n).toLocaleString("en-US");
const fmtMoney = (n) => `$${fmt(n)}`;

export default function ChatGPTAdsBudgetEstimator() {
  const [ref, scrolled] = useScrollAnimate();
  const [budgetPos, setBudgetPos] = useState(posFromBudget(DEFAULT_BUDGET));
  const [cpc, setCpc] = useState(5);
  const [ctrPct, setCtrPct] = useState(0.8);

  const budget = budgetFromPos(budgetPos);
  const ctr = ctrPct / 100;
  // Treats the assumed CPC as the price of every click. In OpenAI's
  // second-price auction a click costs no more than the max bid.
  const clicksPerMonth = budget / cpc;
  const impressionsPerMonth = clicksPerMonth / ctr;
  const dailySpend = budget / 30;

  // These tiers are test-design guidance, not OpenAI thresholds. There has been
  // no account minimum since May 5, 2026; OpenAI documents a per-campaign
  // minimum daily budget that varies by billing currency (US $25/day,
  // October 2026), which every budget on this slider clears.
  let status;
  if (budget <= 5000) {
    status = {
      label: "Directional test",
      color: TEAL,
      bg: "rgba(79,205,196,0.1)",
      border: "rgba(79,205,196,0.3)",
      note: "Enough to read directional signal on 1 to 3 high-intent clusters within 30 days. Each campaign needs at least OpenAI's per-campaign daily minimum (US $25/day).",
    };
  } else if (budget < 25000) {
    status = {
      label: "Focused test budget",
      color: TEAL,
      bg: "rgba(79,205,196,0.1)",
      border: "rgba(79,205,196,0.3)",
      note: "No account minimum to clear: OpenAI documents a per-campaign minimum daily budget that varies by billing currency (US $25/day). Focus this budget on 1 to 3 high-intent clusters before spreading it, and build organic AEO presence alongside the test.",
    };
  } else if (budget < 100000) {
    status = {
      label: "Learning test budget",
      color: TEAL,
      bg: "rgba(79,205,196,0.1)",
      border: "rgba(79,205,196,0.3)",
      note: "Enough for a controlled learning test against 3 to 5 intent clusters. Reserve 20 percent for creative iteration.",
    };
  } else {
    status = {
      label: "Multi-cluster test budget",
      color: GREEN,
      bg: "rgba(34,197,94,0.1)",
      border: "rgba(34,197,94,0.3)",
      note: "Allocate across 5+ intent clusters with separate creative variants per cluster, and pair every paid cluster with a parallel AEO move.",
    };
  }

  return (
    <div
      ref={ref}
      style={{
        margin: "32px 0",
        opacity: scrolled ? 1 : 0,
        transform: scrolled ? "translateY(0)" : "translateY(8px)",
        transition: "opacity 0.5s ease, transform 0.5s ease",
        fontFamily: "var(--body)",
        background: MK_BG,
        borderRadius: 12,
        border: `1px solid ${MK_BD}`,
        padding: "24px 24px",
      }}
    >
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            fontSize: 11,
            color: TEAL,
            fontWeight: 700,
            marginBottom: 4,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          Live Budget Estimator
        </div>
        <div style={{ fontSize: 16, color: TEXT, fontWeight: 600 }}>
          Move the sliders. See what your spend buys.
        </div>
      </div>

      {/* Inputs */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 20,
          marginBottom: 24,
        }}
      >
        <Field
          id="be-budget"
          label="Monthly budget"
          value={fmtMoney(budget)}
        >
          <input
            id="be-budget"
            type="range"
            min={0}
            max={BUDGET_STEPS}
            step={1}
            value={budgetPos}
            onChange={(e) => setBudgetPos(Number(e.target.value))}
            style={sliderStyle}
            aria-label="Monthly budget in US dollars"
            aria-valuetext={fmtMoney(budget)}
          />
          <RangeMarks left="$750" right="$200K" />
        </Field>

        <Field id="be-cpc" label="Assumed cost per click" value={`$${cpc.toFixed(2)}`}>
          <input
            id="be-cpc"
            type="range"
            min={1}
            max={25}
            step={0.25}
            value={cpc}
            onChange={(e) => setCpc(Number(e.target.value))}
            style={sliderStyle}
            aria-label="Assumed cost per click in US dollars, between 1 and 25"
          />
          <RangeMarks left="$1.00" right="$25.00" />
          <Hint>
            OpenAI recommends starting at a $3-$5 maximum bid. Advertisers reported $4.41 to $9.89 per click in September 2026 (US $10.62).
          </Hint>
        </Field>

        <Field id="be-ctr" label="Expected CTR" value={`${ctrPct.toFixed(2)}%`}>
          <input
            id="be-ctr"
            type="range"
            min={0.2}
            max={2.0}
            step={0.1}
            value={ctrPct}
            onChange={(e) => setCtrPct(Number(e.target.value))}
            style={sliderStyle}
            aria-label="Expected click-through rate as a percentage"
          />
          <RangeMarks left="0.2%" right="2.0%" />
          <Hint>Advertisers reported 0.7% to 0.94% (September 2026).</Hint>
        </Field>
      </div>

      {/* Outputs */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <OutputTile label="Clicks per month" value={fmt(clicksPerMonth)} />
        <OutputTile label="Impressions per month" value={fmt(impressionsPerMonth)} />
        <OutputTile
          label="Average daily spend"
          value={`${fmtMoney(dailySpend)}/day`}
          note="One day can run up to 2x"
        />
      </div>

      <div
        role="status"
        aria-live="polite"
        style={{
          background: status.bg,
          border: `1px solid ${status.border}`,
          borderRadius: 8,
          padding: "12px 14px",
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: status.color,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          {status.label}
        </div>
        <div style={{ fontSize: 13, color: TEXT_DIM, lineHeight: 1.6 }}>
          {status.note}
        </div>
      </div>

      <div
        style={{
          fontSize: 10,
          color: TEXT_DIM,
          marginTop: 14,
          fontStyle: "italic",
          lineHeight: 1.6,
        }}
      >
        Estimates only. Clicks assume every click costs the CPC you set; in OpenAI&apos;s relevance-weighted, second-price auction you pay no more than your maximum bid. OpenAI publishes no CPC benchmark and recommends starting a CPC campaign with a maximum bid of $3-$5 USD per click. Delivery also depends on how well your context hints match conversations, ad approval, and the relevance of your title, copy and landing page.
      </div>
    </div>
  );
}

function Field({ id, label, value, children }) {
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: "block",
          fontSize: 11,
          color: TEXT_DIM,
          marginBottom: 4,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </label>
      <div
        style={{
          fontSize: 18,
          color: TEXT,
          fontWeight: 700,
          fontFamily: "var(--mono)",
          marginBottom: 8,
          minHeight: 24,
        }}
      >
        {value}
      </div>
      {children}
    </div>
  );
}

function Hint({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        color: TEXT_DIM,
        marginTop: 8,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

function OutputTile({ label, value, note }) {
  return (
    <div
      style={{
        background: MK_CARD,
        border: `1px solid ${MK_BD}`,
        borderRadius: 8,
        padding: "12px 14px",
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: TEXT_DIM,
          marginBottom: 4,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          color: TEXT,
          fontWeight: 700,
          fontFamily: "var(--mono)",
        }}
      >
        {value}
      </div>
      {note && (
        <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 4 }}>
          {note}
        </div>
      )}
    </div>
  );
}

function RangeMarks({ left, right }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        fontSize: 10,
        color: TEXT_DIM,
        marginTop: 4,
        fontFamily: "var(--mono)",
      }}
    >
      <span>{left}</span>
      <span>{right}</span>
    </div>
  );
}

const sliderStyle = {
  width: "100%",
  height: 4,
  borderRadius: 2,
  background: "rgba(255,255,255,0.12)",
  outline: "none",
  WebkitAppearance: "none",
  appearance: "none",
  cursor: "pointer",
  accentColor: TEAL,
};
